import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Download, Loader2, AlertTriangle, Share2, Copy, Trash2, X, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { useDocMeta } from "../../lib/useDocMeta";
import { renderBriefBody, executiveSummaryText } from "../public/briefBody";
import { ComplianceSnapshot } from "./Pursuits";

// -------- Story #4.5 — Authenticated Executive Opportunity Brief --------
// Shares the same visual body renderer as the public /brief/:token view via
// pages/public/briefBody. Adds three customer actions:
//   • Print / Download PDF (browser print dialog)
//   • Share Link (create/list/revoke — Release 0.1 core feature)
//   • Copy Executive Summary (copies a plain-text digest to clipboard)

export default function CustomerPursuitBrief() {
  const { id } = useParams();
  const { custApi } = useCustomerAuth();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  useDocMeta({ title: "Executive Opportunity Brief · GovBidIQ", path: `/app/pursuits/${id}/brief` });

  useEffect(() => {
    custApi
      .get(`/customer/reports/${id}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [custApi, id]);

  const onPrint = () => window.print();

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(executiveSummaryText(doc));
      toast.success("Executive summary copied to clipboard");
    } catch {
      toast.error("Copy failed. Your browser may block clipboard access.");
    }
  };

  if (err) return (
    <div data-testid="cust-brief-error" className="max-w-3xl">
      <div className="border border-rose-500/40 bg-rose-500/5 p-4 inline-flex items-start gap-2 mb-4">
        <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5" />
        <div className="text-sm text-slate-300">{err}</div>
      </div>
      <Link to="/app/pursuits" className="block font-mono text-[10px] tracking-widest uppercase text-sky-400">← Back to pursuits</Link>
    </div>
  );
  if (!doc) return <Loader2 className="w-4 h-4 animate-spin text-slate-500" />;

  return (
    <div className="max-w-[900px] mx-auto" data-testid="cust-pursuit-brief">
      {/* Toolbar — hidden on print */}
      <div className="brief-toolbar flex items-center justify-between gap-3 mb-6 flex-wrap print:hidden">
        <Link to={`/app/pursuits/${id}`} className="font-mono text-[10px] tracking-widest uppercase text-slate-500 hover:text-sky-400 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to pursuit
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={copySummary} data-testid="cust-brief-copy-summary"
            className="inline-flex items-center gap-2 border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5">
            <Copy className="w-3.5 h-3.5" /> Copy Summary
          </button>
          <button onClick={() => setShareOpen(true)} data-testid="cust-brief-open-share"
            className="inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            <Share2 className="w-3.5 h-3.5" /> Share Link
          </button>
          <button onClick={onPrint} data-testid="cust-brief-print"
            className="inline-flex items-center gap-2 border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
            <Printer className="w-3.5 h-3.5" strokeWidth={2.5} /> Print
          </button>
          <button onClick={onPrint} data-testid="cust-brief-download"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5"
            title="Use your browser's Save as PDF option in the print dialog.">
            <Download className="w-3.5 h-3.5" strokeWidth={2.5} /> Download PDF
          </button>
        </div>
      </div>

      {/* Printable brief body */}
      <article id="brief-print-root" className="bg-white shadow-xl p-10 print:shadow-none print:p-6">
        {renderBriefBody(doc, { publicMode: false })}
      </article>

      <style>{`
        @media print {
          @page { size: Letter; margin: 0.5in; }
          html, body { background: #ffffff !important; }
          body * { visibility: hidden !important; }
          #brief-print-root, #brief-print-root * { visibility: visible !important; }
          #brief-print-root { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; padding: 0 !important; }
          .brief-toolbar, nav, aside { display: none !important; }
        }
      `}</style>

      {/* Full compliance detail (on-screen only, dark theme) */}
      <div className="mt-8 print:hidden">
        <details className="text-slate-400 text-xs">
          <summary className="cursor-pointer font-mono text-[10px] tracking-[0.2em] uppercase text-sky-400">Show full compliance detail (on-screen only)</summary>
          <div className="mt-3">
            <ComplianceSnapshot report={doc.report || {}} />
          </div>
        </details>
      </div>

      {shareOpen && <ShareLinkModal open={shareOpen} onClose={() => setShareOpen(false)} pursuitId={id} custApi={custApi} />}
    </div>
  );
}

// -------- Share Link Modal (Release 0.1 core feature) --------
function ShareLinkModal({ open, onClose, pursuitId, custApi }) {
  const [links, setLinks] = useState([]);
  const [ttl, setTtl] = useState(30);
  const [busy, setBusy] = useState(false);
  const publicBase = window.location.origin;

  const load = () => {
    custApi.get(`/customer/pursuits/${pursuitId}/share`)
      .then((r) => setLinks(r.data.links || []))
      .catch(() => setLinks([]));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await custApi.post(`/customer/pursuits/${pursuitId}/share`, { ttl_days: Number(ttl) || 30 });
      toast.success("Share link created");
      const url = `${publicBase}${res.data.url_path}`;
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); } catch { /* ignore */ }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create link");
    } finally { setBusy(false); }
  };

  const revoke = async (token) => {
    setBusy(true);
    try {
      await custApi.delete(`/customer/pursuits/${pursuitId}/share/${token}`);
      toast.success("Link revoked");
      load();
    } catch (e) {
      toast.error("Failed to revoke");
    } finally { setBusy(false); }
  };

  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(`${publicBase}/brief/${token}`); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  if (!open) return null;
  const activeLinks = links.filter((l) => !l.revoked_at);
  const revokedLinks = links.filter((l) => !!l.revoked_at);

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cust-brief-share-modal">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-soft)] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[var(--border-soft)] p-4 flex items-center justify-between">
          <div>
            <div className="tick-label text-sky-400 mb-1">/ Share Executive Brief</div>
            <div className="font-display font-bold text-slate-100">Read-only · Expiring · Revocable</div>
          </div>
          <button onClick={onClose} data-testid="cust-brief-share-close" className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="border border-sky-500/40 bg-sky-500/5 p-4">
            <p className="text-xs text-slate-300 mb-3 leading-relaxed">
              Anyone with the link can view a read-only version of this brief until it expires or is revoked. The brief is watermarked <strong>Prepared by BidNoBidIQ</strong>.
            </p>
            <div className="flex items-end gap-2">
              <label className="flex-1">
                <span className="tick-label mb-1.5 inline-block">Expires in (days)</span>
                <input
                  type="number" min={1} max={365} value={ttl} onChange={(e) => setTtl(e.target.value)}
                  data-testid="cust-brief-share-ttl"
                  className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] focus:border-sky-500/70 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
              </label>
              <button onClick={create} disabled={busy} data-testid="cust-brief-share-create"
                className="bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5 inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                Create link
              </button>
            </div>
          </div>

          <div>
            <div className="tick-label text-sky-400 mb-2">Active links ({activeLinks.length})</div>
            {activeLinks.length === 0 ? (
              <p className="text-slate-500 text-sm italic">No active share links.</p>
            ) : (
              <ul className="space-y-2" data-testid="cust-brief-share-active-list">
                {activeLinks.map((l) => (
                  <li key={l.token} className="border border-[var(--border-soft)] bg-[var(--bg-primary)] p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-slate-100 truncate">{publicBase}/brief/{l.token}</div>
                      <div className="font-mono text-[10px] text-slate-500 mt-1">
                        Created {new Date(l.created_at).toLocaleDateString()} · Expires {new Date(l.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button onClick={() => copyLink(l.token)} data-testid={`cust-brief-share-copy-${l.token}`}
                      className="border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[10px] tracking-widest uppercase px-2.5 py-1.5 inline-flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <button onClick={() => revoke(l.token)} data-testid={`cust-brief-share-revoke-${l.token}`}
                      className="border border-rose-500/40 hover:bg-rose-500/10 text-rose-300 font-mono text-[10px] tracking-widest uppercase px-2.5 py-1.5 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {revokedLinks.length > 0 && (
            <details className="text-slate-400">
              <summary className="cursor-pointer font-mono text-[10px] tracking-widest uppercase text-slate-500">Revoked ({revokedLinks.length})</summary>
              <ul className="mt-2 space-y-1">
                {revokedLinks.map((l) => (
                  <li key={l.token} className="text-xs text-slate-500 font-mono truncate">{l.token} · revoked {new Date(l.revoked_at).toLocaleDateString()}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
